import { jwtDecode } from 'jwt-decode';
import { User } from '../types';

const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const PRIMARY_ORDER = [
  'owner',
  'courseadmin',
  'courseowner',
  'gigadmin',
  'gigcreator',
  'academyadmin',
  'interviewadmin',
  'useradmin',
  'user',
];

export function rolesFromAccessToken(token?: string | null): string[] {
  if (!token) {
    return [];
  }

  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    const raw = decoded[ROLE_CLAIM] ?? decoded.role ?? decoded.roles;
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item)).filter(Boolean);
    }
    if (typeof raw === 'string' && raw.trim()) {
      return [raw];
    }
    return [];
  } catch {
    return [];
  }
}

function primaryRole(roles: string[]): string {
  const lower = roles.map((role) => role.toLowerCase());
  for (const preferred of PRIMARY_ORDER) {
    const index = lower.indexOf(preferred);
    if (index >= 0) {
      return roles[index];
    }
    if (preferred === 'courseowner') {
      const legacy = lower.indexOf('coursecreator');
      if (legacy >= 0) {
        return roles[legacy];
      }
    }
  }
  return roles[0];
}

/** Keep JWT role claims when profile GET only returns a single Role (often User). */
export function withTokenRoles(user: User, token?: string | null): User {
  const merged: string[] = [];
  const seen = new Set<string>();
  for (const role of [...(user.roles ?? []), user.role, ...rolesFromAccessToken(token)]) {
    if (!role) {
      continue;
    }
    const key = role.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(role);
  }

  if (merged.length === 0) {
    return user;
  }

  return { ...user, roles: merged, role: primaryRole(merged) };
}
